/**
 * What the dishes on a tab are doing, worked out once for the order screen.
 *
 * The tab used to say this in three places at once: a held tag on each line, a
 * button on each course heading, and a strip of "Up · Course 1" above the
 * list. The strip took the room the dishes needed, and once a course was sent
 * a waiter could see that it was up but not what was in it. Now each course
 * heading carries its own state, every dish stays listed under it, and each
 * line carries one small mark.
 *
 * Pure, so it can be pinned by a test: the screen only draws what this says.
 */

import type { OrderLine } from '@restaurant/api-client';

import type { FoodUp } from './readiness';

/** Where one course is, in the words its heading uses. */
export type CourseState =
  /** Every line on it is voided: nothing to say. */
  | { kind: 'empty' }
  /** Dishes waiting to be sent. `all` when none of the course has gone yet. */
  | { kind: 'held'; held: number; all: boolean }
  /** Every station cooking it has bumped it. */
  | { kind: 'up'; readyAt: string | null }
  /** With the kitchen, and how far along. */
  | { kind: 'cooking'; stations: number; stationsReady: number }
  /** Sent, and the kitchen has said nothing yet. Only on a plan that holds courses. */
  | { kind: 'sent' }
  /** No course firing and nothing heard from the kitchen: nothing worth a word. */
  | { kind: 'quiet' };

export interface CourseGroup {
  courseNo: number;
  /** In the order they were taken. */
  lines: OrderLine[];
  /** Lines not voided. */
  live: number;
  /** Lines still waiting to be sent. Nought where nothing waits. */
  held: number;
  state: CourseState;
}

/**
 * A tab's lines by course, in course order, each with what its heading says.
 *
 * `firing` is whether this tab holds dishes until a course is sent — a plan
 * with course firing, and not a counter order (which goes as it is rung in).
 */
export function groupTab(lines: OrderLine[], firing: boolean, food: FoodUp): CourseGroup[] {
  const byCourse = new Map<number, OrderLine[]>();
  for (const line of lines) {
    const group = byCourse.get(line.course_no);
    if (group) group.push(line);
    else byCourse.set(line.course_no, [line]);
  }

  return [...byCourse.entries()]
    .sort(([a], [b]) => a - b)
    .map(([courseNo, courseLines]) => {
      const live = courseLines.filter((l) => l.state !== 'voided');
      const held = firing ? live.filter((l) => l.state === 'ordered').length : 0;
      return {
        courseNo,
        lines: courseLines,
        live: live.length,
        held,
        state: stateOf(courseNo, live.length, held, firing, food),
      };
    });
}

function stateOf(
  courseNo: number,
  live: number,
  held: number,
  firing: boolean,
  food: FoodUp,
): CourseState {
  if (live === 0) return { kind: 'empty' };
  // Something still waiting outranks what the kitchen says about the rest:
  // a forgotten side added to a course that is up is the thing to act on.
  if (held > 0) return { kind: 'held', held, all: held === live };
  const ready = food.ready.find((c) => c.course_no === courseNo);
  if (ready) return { kind: 'up', readyAt: ready.ready_at };
  const waiting = food.waiting.find((c) => c.course_no === courseNo);
  if (waiting) {
    return {
      kind: 'cooking',
      stations: waiting.stations,
      stationsReady: waiting.stations_ready,
    };
  }
  return firing ? { kind: 'sent' } : { kind: 'quiet' };
}

/**
 * Whether the course headings earn their space.
 *
 * Once there is more than one course, or each course has to be sent, or the
 * kitchen has something to say about one — otherwise a single heading reading
 * "Course 1" over a counter order is a line of nothing.
 */
export function showCourseHeads(groups: CourseGroup[], firing: boolean): boolean {
  if (groups.length > 1) return true;
  if (firing && groups.length > 0) return true;
  return groups.some((g) => g.state.kind === 'up' || g.state.kind === 'cooking');
}

/**
 * The course the till's one "Send" button sends: the earliest with anything
 * waiting. Courses go in order nearly always; a later one can still be sent
 * from its own heading.
 */
export function nextToSend(groups: CourseGroup[]): CourseGroup | null {
  return groups.find((g) => g.held > 0) ?? null;
}

/** The small mark at the end of a line. Null where it would say nothing new. */
export type LineMark = 'voided' | 'held' | 'up' | 'sent' | null;

export function lineMark(line: OrderLine, group: CourseGroup, firing: boolean): LineMark {
  if (line.state === 'voided') return 'voided';
  if (firing && line.state === 'ordered') return 'held';
  if (group.state.kind === 'up') return 'up';
  // Without course firing every dish goes as it is rung in, so "sent" on each
  // would be true of every line on every tab and tell nobody anything.
  return firing ? 'sent' : null;
}

/**
 * The second line under a dish: its choices, its seat, in one quiet run.
 * The note is drawn apart, in its own voice, so it is not part of this.
 */
export function lineDetail(line: OrderLine): string {
  const parts = line.modifiers.map((m) => m.name);
  if (line.seat_no !== null) parts.push(`Seat ${line.seat_no}`);
  return parts.join(' · ');
}
